import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import type { User } from '../../lib/types';
import type { OrgPilot } from '../../hooks/useOrgPilots';
import type { PilotTarget } from '../../hooks/usePilotRatings';
import PilotIdentityForm from './PilotIdentityForm';
import PilotRatingsTable from './PilotRatingsTable';
import PilotLanguagesTable from './PilotLanguagesTable';

type TabKey = 'identity' | 'ratings' | 'languages';

interface Props {
  userId?: string;
  orgPilotId?: string;
  onClose?: () => void;
}

/**
 * Full licence / rating / language editor for either a registered user
 * or an ad-hoc org_pilot. Exactly one of userId / orgPilotId must be set.
 *
 * RLS decides who can actually save: airline admins always; registered
 * pilots editing their own row.
 */
export default function PilotProfileEditor({ userId, orgPilotId, onClose }: Props) {
  const [record, setRecord] = useState<User | OrgPilot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('identity');

  const kind: 'user' | 'org_pilot' | null = userId
    ? 'user'
    : orgPilotId
      ? 'org_pilot'
      : null;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!kind) {
        setError('Either userId or orgPilotId must be provided');
        setLoading(false);
        return;
      }
      setLoading(true);
      const table = kind === 'user' ? 'users' : 'org_pilots';
      const id = kind === 'user' ? userId! : orgPilotId!;
      const { data, error: err } = await supabase
        .from(table)
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (cancelled) return;
      if (err) {
        setError(err.message);
      } else if (!data) {
        setError('Pilot record not found');
      } else {
        setRecord(data as User | OrgPilot);
        setError(null);
      }
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [kind, userId, orgPilotId]);

  if (loading) return <div className="p-6 text-gray-500">Loading pilot…</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!record || !kind) return null;

  const target: PilotTarget =
    kind === 'user'
      ? { kind: 'user', userId: (record as User).id }
      : { kind: 'org_pilot', orgPilotId: (record as OrgPilot).id };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
        <div>
          <h2 className="text-lg font-semibold text-[#0B2C45]">{record.full_name}</h2>
          <p className="text-xs text-gray-500">
            {kind === 'user' ? 'Registered pilot' : 'Ad-hoc pilot (org_pilots)'} · ID {record.id}
          </p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Close
          </button>
        )}
      </div>

      <nav className="flex gap-1 border-b border-gray-200 bg-gray-50 px-4">
        {(
          [
            ['identity', 'Identity'],
            ['ratings', 'Ratings'],
            ['languages', 'Languages'],
          ] as [TabKey, string][]
        ).map(([key, label]) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                active
                  ? 'border-[#4A90E2] text-[#0B2C45]'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          );
        })}
      </nav>

      <div className="p-6">
        {tab === 'identity' && (
          <PilotIdentityForm
            target={
              kind === 'user'
                ? { kind: 'user', record: record as User }
                : { kind: 'org_pilot', record: record as OrgPilot }
            }
            onSaved={() => {
              // Refetch to keep editor in sync with DB
              supabase
                .from(kind === 'user' ? 'users' : 'org_pilots')
                .select('*')
                .eq('id', record.id)
                .maybeSingle()
                .then(({ data }) => {
                  if (data) setRecord(data as User | OrgPilot);
                });
            }}
          />
        )}
        {tab === 'ratings' && <PilotRatingsTable target={target} />}
        {tab === 'languages' && <PilotLanguagesTable target={target} />}
      </div>
    </div>
  );
}
